import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Plus, Search, ChevronDown, AlertCircle, CheckCircle2, ArrowLeft, Eye, Edit2, X } from 'lucide-react';
import { ITCaFilters } from './components/ITCaFilters';
import type { ITCaFilterState } from './components/ITCaFilters';
import { AddEditITCaModal } from './components/AddEditITCaModal';
import type { ITCaRecord, ITCaFormData } from './components/AddEditITCaModal';
import {
  getITCaList,
  createITCa,
  updateITCa,
  type ApiITCa,
} from '../../../api/itca';
import { TableLoader } from '../../../components/Common/Loader';
import CSVButton from '../../../components/Common/CSVButton';
import { exportToCSV } from '../../../utils/csvExport';

const normalizeITCaRecord = (item: ApiITCa | any): ITCaRecord => {
  const statusVal = item.status;
  const isActive =
    statusVal === 1 ||
    statusVal === '1' ||
    statusVal === 'Active' ||
    statusVal === true ||
    item.is_active === 1;

  let formattedDate = '-';
  const rawDate = item.createdOn || item.createdAt || item.created_at;
  if (rawDate) {
    try {
      const d = new Date(rawDate);
      if (!isNaN(d.getTime())) {
        formattedDate = d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      } else {
        formattedDate = String(rawDate);
      }
    } catch {
      formattedDate = String(rawDate);
    }
  }

  const createdByVal = item.createdBy || item.created_by;
  return {
    id: item.id,
    name: String(item.name || ''),
    description: String(item.description || ''),
    status: isActive ? 'Active' : 'Inactive',
    createdOn: formattedDate,
    createdBy: createdByVal !== undefined && createdByVal !== null ? String(createdByVal) : '',
  };
};

export const ITCa: React.FC = () => {
  const [records, setRecords] = useState<ITCaRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [entriesCount, setEntriesCount] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [tableSearch, setTableSearch] = useState<string>('');

  // Filters State
  const [filters, setFilters] = useState<ITCaFilterState>({
    searchQuery: '',
    status: '',
  });

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ITCaRecord | null>(null);
  const [viewingRecord, setViewingRecord] = useState<ITCaRecord | null>(null);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getITCaList();

      let rawList: any[] = [];
      if (res && Array.isArray(res.data)) {
        rawList = res.data;
      } else if (Array.isArray(res)) {
        rawList = res;
      } else if (res && typeof res === 'object' && Array.isArray((res as any).itca)) {
        rawList = (res as any).itca;
      } else if (res && typeof res === 'object' && Array.isArray((res as any).records)) {
        rawList = (res as any).records;
      }

      const normalized = rawList.map(normalizeITCaRecord);
      setRecords(normalized);
    } catch (err: any) {
      console.error('Error fetching ITCA records:', err);
      setError(err?.message || 'Failed to load ITCA records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Auto clear notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((rec) => {
      if (filters.status && rec.status !== filters.status) {
        return false;
      }
      if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase();
        const matchesName = String(rec.name || '').toLowerCase().includes(q);
        const matchesDesc = String(rec.description || '').toLowerCase().includes(q);
        const matchesCreatedBy = String(rec.createdBy || '').toLowerCase().includes(q);
        if (!matchesName && !matchesDesc && !matchesCreatedBy) return false;
      }
      if (tableSearch) {
        const q = tableSearch.toLowerCase();
        const matchesName = String(rec.name || '').toLowerCase().includes(q);
        const matchesDesc = String(rec.description || '').toLowerCase().includes(q);
        const matchesCreatedBy = String(rec.createdBy || '').toLowerCase().includes(q);
        if (!matchesName && !matchesDesc && !matchesCreatedBy) return false;
      }
      return true;
    });
  }, [records, filters, tableSearch]);

  // Pagination calculation
  const totalEntries = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / entriesCount));
  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * entriesCount;
    return filteredRecords.slice(start, start + entriesCount);
  }, [filteredRecords, currentPage, entriesCount]);

  // Reset current page if filters or count change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, tableSearch, entriesCount]);

  // Modal Actions
  const handleOpenAddModal = () => {
    setEditingRecord(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (rec: ITCaRecord) => {
    setEditingRecord(rec);
    setIsModalOpen(true);
  };

  const handleSaveRecord = async (data: ITCaFormData) => {
    try {
      setIsSubmitting(true);
      const statusNum = data.status === 'Active' ? 1 : 0;
      if (editingRecord && editingRecord.id) {
        await updateITCa({
          id: editingRecord.id,
          name: data.name,
          description: data.description,
          status: statusNum,
        });
        setNotification({
          type: 'success',
          message: `ITCA record "${data.name}" updated successfully!`,
        });
      } else {
        await createITCa({
          name: data.name,
          description: data.description,
          status: statusNum,
        });
        setNotification({
          type: 'success',
          message: `ITCA record "${data.name}" created successfully!`,
        });
      }

      setIsModalOpen(false);
      await fetchRecords();
    } catch (err: any) {
      console.error('Error saving ITCA record:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportExcel = () => {
    if (filteredRecords.length === 0) return;
    const headers = ['ID', 'Name', 'Description', 'Status', 'Created On', 'Created By'];
    const rows = filteredRecords.map((r) => [
      r.id,
      r.name,
      r.description || '',
      r.status,
      r.createdOn,
      r.createdBy,
    ]);
    exportToCSV('itca_records', headers, rows);
  };

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Toast Notification */}
      {notification && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl border text-xs font-bold transition-all transform animate-fadeIn ${
            notification.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 shadow-emerald-500/10'
              : 'bg-rose-50 text-rose-800 border-rose-200 shadow-rose-500/10'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          )}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Title and Back Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            ITCA
          </h1>
        </div>

        {/* Top Buttons */}
        <div className="flex items-center gap-3">
          {/* + Add ITCA Record Button */}
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl shadow-md shadow-[#C1272D]/20 flex items-center gap-2 cursor-pointer transition-all active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>Add ITCA Record</span>
          </button>

          <CSVButton onClick={handleExportExcel} />
        </div>
      </div>

      {/* Filters Toolbar */}
      <ITCaFilters
        filters={filters}
        onFilterChange={setFilters}
        onReset={() => setFilters({ searchQuery: '', status: '' })}
      />

      {/* Table Card */}
      <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
        {/* Card Header & Controls */}
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white">
          <h3 className="text-base font-bold text-slate-800">ITCA Records</h3>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Show entries */}
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              <span>Show</span>
              <div className="relative">
                <select
                  value={entriesCount}
                  onChange={(e) => setEntriesCount(Number(e.target.value))}
                  className="appearance-none bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 pr-7 text-xs font-bold text-slate-700 focus:outline-none cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-2.5 pointer-events-none" />
              </div>
              <span>entries</span>
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search in table..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
              />
            </div>
          </div>
        </div>

        {/* Table Canvas */}
        <div className="overflow-x-auto min-h-[300px] relative">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <TableLoader />
            </div>
          ) : error ? (
            <div className="py-16 text-center text-rose-500 text-xs font-semibold flex flex-col items-center gap-3">
              <AlertCircle className="w-8 h-8 text-rose-400" />
              <p>{error}</p>
              <button
                onClick={fetchRecords}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                Retry
              </button>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  <th className="py-3.5 px-6">ID</th>
                  <th className="py-3.5 px-6">ITCA Name</th>
                  <th className="py-3.5 px-6">Description</th>
                  <th className="py-3.5 px-6">Status</th>
                  <th className="py-3.5 px-6">Created On</th>
                  <th className="py-3.5 px-6">Created By</th>
                  <th className="py-3.5 px-6 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                {paginatedRecords.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-4 px-6 font-medium text-slate-400">{item.id}</td>
                    <td className="py-4 px-6 font-bold text-slate-800">{item.name}</td>
                    <td className="py-4 px-6 font-medium text-slate-600">{item.description || '-'}</td>
                    <td className="py-4 px-6 text-xs text-[#828282]">{item.status}</td>
                    <td className="py-4 px-6 font-medium text-slate-500 whitespace-nowrap">
                      {item.createdOn}
                    </td>
                    <td className="py-4 px-6 font-medium text-slate-700 whitespace-nowrap">
                      {item.createdBy || '-'}
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setViewingRecord(item)}
                          className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenEditModal(item)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {paginatedRecords.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-400 font-medium">
                      No ITCA records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Table Footer Pagination */}
        {!loading && !error && (
          <div className="p-5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white text-xs font-semibold text-slate-500">
            <span>
              Showing {totalEntries === 0 ? 0 : (currentPage - 1) * entriesCount + 1} to{' '}
              {Math.min(currentPage * entriesCount, totalEntries)} of {totalEntries} entries
            </span>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 font-bold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2))
                .map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-lg font-bold flex items-center justify-center transition-all cursor-pointer ${
                      currentPage === pageNum
                        ? 'bg-[#C1272D] text-white shadow-xs'
                        : 'border border-slate-200 hover:bg-slate-50 text-slate-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-[#C1272D] font-bold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewingRecord && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-extrabold text-slate-800">{viewingRecord.name}</h3>
                <p className="text-xs font-mono font-medium text-slate-400">ITCA ID: {viewingRecord.id}</p>
              </div>
              <button onClick={() => setViewingRecord(null)} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4 text-xs">
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Name</span>
                <p className="text-sm font-bold text-slate-900">{viewingRecord.name}</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Description</span>
                <p className="text-xs font-medium text-slate-700">{viewingRecord.description || 'No description.'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Status</span>
                  <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${viewingRecord.status === 'Active' ? 'bg-emerald-100/80 text-emerald-700' : 'bg-rose-100/80 text-rose-700'}`}>{viewingRecord.status}</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Created On</span>
                  <p className="text-xs font-medium text-slate-700">{viewingRecord.createdOn}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t border-slate-100">
              <button onClick={() => setViewingRecord(null)} className="px-6 py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs rounded-xl cursor-pointer transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AddEditITCaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveRecord}
        initialData={editingRecord}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default ITCa;
